import React, { useEffect } from 'react';
import { useDispatch, useSelector, connect } from 'react-redux';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { ReduxSelectField, ReduxTextField, PhoneField, CheckboxField, DateField } from 'common/components';
import { withTheme } from 'common/styling/theme';
import { getCountries } from 'client/KYC/_redux';
import { getCrmUserKYC } from 'crm/users/_redux';

import { ExperienceFields, SelectCountryField } from 'backoffice/users/_components/UserKYCForm/fields';
import { staticStyles, getDynamicStyles } from 'backoffice/users/_components/UserKYCForm/style';

const TabKYCForm = ({
  countries,
  states,
  experienceForex,
  experienceDerivative,
  experienceSecurities,
  experienceInvestmentFund,
  removeKycTaxCheckbox,
  personalIdInKycForm,
  intl,
  theme,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const countriesOptions = countries.map(option => ({
    value: option.gid,
    label: option.name,
  }));

  const statesOptions = states.map(option => ({
    value: option.gid,
    label: option.name,
  }));

  const generateOptionsFromTranslations = keys =>
    keys.map(key => ({ value: key, label: intl.formatMessage({ id: key }) }));

  // 只读模式，所有字段强制 disabled，checkbox 除外
  const disabled = true;
  const checkboxDisabled = false;

  return (
    <form className="UserKYCForm">
      <div className="UserKYCForm__inputs-inner">
        <FormattedMessage id="generalInfo">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="UserKYCForm__inputs-block">
          <Field component={ReduxTextField} type="text" name="firstName" textId="kycFirstName" disabled={disabled} />
          <Field component={ReduxTextField} type="text" name="middleName" textId="kycMiddleName" disabled={disabled} />
          <Field component={ReduxTextField} type="text" name="lastName" textId="kycLastName" disabled={disabled} />
        </div>

        <div className={classNames('UserKYCForm__inputs-block', { 'block-two-inp': !personalIdInKycForm })}>
          <Field
            component={ReduxSelectField}
            name="taxResidence"
            textId="kycTaxResidence"
            options={countriesOptions}
            disabled={disabled}
            searchable
          />
          <Field
            component={DateField}
            type="date"
            name="birthday"
            textId="kycBirthday"
            placeholder="dd.mm.yyyy"
            disabled={disabled}
          />
          {personalIdInKycForm && (
            <Field
              component={ReduxTextField}
              type="text"
              name="personalId"
              textId="kycPassportNumber"
              disabled={disabled}
            />
          )}
        </div>

        <div className="UserKYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxSelectField}
            name="nationality"
            textId="kycNationality"
            options={countriesOptions}
            disabled={disabled}
            searchable
          />
          <Field component={PhoneField} name="phone" textId="kycPhone" disabled={disabled} />
        </div>

        {!removeKycTaxCheckbox && (
          <Field component={CheckboxField} name="usTax" textId="kycUsTax" disabled={disabled} />
        )}
      </div>

      <div className="UserKYCForm__inputs-inner">
        <FormattedMessage id="kycAddress">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="UserKYCForm__inputs-block">
          <Field
            component={SelectCountryField}
            name="address.country"
            textId="kycCountry"
            options={countriesOptions}
            disabled={disabled}
          />
          <Field
            component={ReduxSelectField}
            name="address.state"
            textId="kycState"
            options={statesOptions}
            disabled={disabled}
            searchable
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="address.postCode"
            textId="kycPostCode"
            disabled={disabled}
          />
        </div>

        <div className="UserKYCForm__inputs-block block-two-inp">
          <Field component={ReduxTextField} type="text" name="address.city" textId="kycCity" disabled={disabled} />
          <Field
            component={ReduxTextField}
            type="text"
            name="address.address"
            textId="kycAddress"
            disabled={disabled}
          />
        </div>
      </div>

      <div className="UserKYCForm__inputs-inner">
        <FormattedMessage id="kycFinDetails">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="UserKYCForm__inputs-block">
          <Field
            component={ReduxSelectField}
            name="finance.capital"
            textId="kycNetCapital"
            options={generateOptionsFromTranslations([
              'kycAmountBelow10',
              'kycAmount10to50',
              'kycAmount50to100',
              'kycAmountOver100',
            ])}
            disabled={disabled}
          />
          <Field
            component={ReduxSelectField}
            name="finance.income"
            textId="kycAnnualIncome"
            options={generateOptionsFromTranslations([
              'kycAmountBelow10',
              'kycAmount10to50',
              'kycAmount50to100',
              'kycAmountOver100',
            ])}
            disabled={disabled}
          />
          <Field
            component={ReduxSelectField}
            name="finance.commitments"
            textId="kycMonFinCommit"
            options={generateOptionsFromTranslations([
              'kycCommitmentsBelow20',
              'kycCommitments21to40',
              'kycCommitments41to60',
              'kycCommitments61to80',
              'kycCommitmentsOver80',
            ])}
            disabled={disabled}
          />
        </div>

        <div className="UserKYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxTextField}
            type="text"
            name="finance.investments"
            textId="kycApproxInvestPerAnn"
            disabled={disabled}
          />
          <Field
            component={ReduxSelectField}
            name="finance.purpose"
            textId="kycPurpose"
            options={generateOptionsFromTranslations([
              'kycPurposeInvestment',
              'kycPurposeHedging',
              'kycPurposeSpecTrading',
            ])}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="UserKYCForm__inputs-inner">
        <FormattedMessage id="kycTypeOfActivity">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="UserKYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxSelectField}
            name="activity.employment"
            textId="kycEmplStatus"
            options={generateOptionsFromTranslations([
              'kycEmploymentEmpl',
              'kycEmploymentUnempl',
              'kycEmploymentSelfEmpl',
              'kycEmploymentRet',
              'kycEmploymentStud',
            ])}
            disabled={disabled}
          />
          <Field
            component={ReduxSelectField}
            name="activity.source"
            textId="kycSourceOfFunds"
            options={generateOptionsFromTranslations([
              'kycSourceProfAct',
              'kycSourceSavings',
              'kycSourceFam',
              'kycSourceInvest',
              'kycSourceBorrow',
              'kycSourcePension',
              'kycSourceOther',
            ])}
            disabled={disabled}
          />
        </div>

        <div className="UserKYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxSelectField}
            name="activity.business"
            textId="kycNatureOfBusiness"
            options={generateOptionsFromTranslations([
              'kycBizAccount',
              'kycBizSecr',
              'kycBizAgric',
              'kycBizArt',
              'kycBizBank',
              'kycBizCatHosp',
              'kycBizMedia',
              'kycBizEdu',
              'kycBizEmerg',
              'kycBizEngin',
              'kycBizHealth',
              'kycBizIT',
              'kycBizLegal',
              'kycBizLeisure',
              'kycBizManuf',
              'kycBizPR',
              'kycBizProp',
              'kycBizSales',
              'kycBizSocial',
              'kycBizTelecom',
              'kycBizTransport',
              'kycBizOther',
            ])}
            disabled={disabled}
            searchable
          />
          <Field
            component={ReduxSelectField}
            name="activity.education"
            textId="kycLvlOfEdu"
            options={generateOptionsFromTranslations(['kycEduHS', 'kycEduColl', 'kycEduUnivers', 'kycEduOther'])}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="UserKYCForm__inputs-inner">
        <FormattedMessage id="kycTradingExp">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="UserKYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.forex"
            textId="kycForexCdfs"
            disabled={disabled}
            checkboxDisabled={checkboxDisabled}
            active={experienceForex}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>

        <div className="UserKYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.derivative"
            textId="kycDerivativeInstr"
            disabled={disabled}
            checkboxDisabled={checkboxDisabled}
            active={experienceDerivative}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>

        <div className="UserKYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.securities"
            textId="kycTransfSec"
            disabled={disabled}
            checkboxDisabled={checkboxDisabled}
            active={experienceSecurities}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>

        <div className="UserKYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.investmentFund"
            textId="kycUnitsCollectiveInv"
            disabled={disabled}
            checkboxDisabled={checkboxDisabled}
            active={experienceInvestmentFund}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>
      </div>

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </form>
  );
};

const selector = formValueSelector('CrmUserKYCForm');

const ConnectedTabKYCForm = compose(
  withTheme(),
  injectIntl,
  connect((state, ownProps) => ({
    experienceForex: Boolean(selector(state, 'experience.forex.checkbox') ?? ownProps.experienceForex),
    experienceDerivative: Boolean(selector(state, 'experience.derivative.checkbox') ?? ownProps.experienceDerivative),
    experienceSecurities: Boolean(selector(state, 'experience.securities.checkbox') ?? ownProps.experienceSecurities),
    experienceInvestmentFund: Boolean(
      selector(state, 'experience.investmentFund.checkbox') ?? ownProps.experienceInvestmentFund
    ),
    removeKycTaxCheckbox: state.interfaceConfig.features.includes('removeKycTaxCheckbox'),
    personalIdInKycForm: state.interfaceConfig.features.includes('personalIdInKycForm'),
    countries: state.client.kyc.countries,
    states: state.client.kyc.states,
  })),
  reduxForm({
    form: 'CrmUserKYCForm',
    onSubmit: () => {},
  })
)(TabKYCForm);

const TabKYC = () => {
  const dispatch = useDispatch();
  const { userData, userKYC, userKYCAreLoaded } = useSelector(state => state.crm.users);

  useEffect(() => {
    if (userData.userId) {
      dispatch(getCrmUserKYC.request(userData.userId));
      dispatch(getCountries.request());
    }
  }, [userData.userId]);

  if (!userKYCAreLoaded) return null;

  const hasExperienceData = experience => {
    if (!experience) return false;
    return experience.checkbox || experience.frequency || experience.training || experience.volume || experience.years;
  };

  const modifiedInitialValues = {
    ...userKYC,
    experience: {
      ...userKYC?.experience,
      forex: {
        ...userKYC?.experience?.forex,
        checkbox: hasExperienceData(userKYC?.experience?.forex),
      },
      derivative: {
        ...userKYC?.experience?.derivative,
        checkbox: hasExperienceData(userKYC?.experience?.derivative),
      },
      securities: {
        ...userKYC?.experience?.securities,
        checkbox: hasExperienceData(userKYC?.experience?.securities),
      },
      investmentFund: {
        ...userKYC?.experience?.investmentFund,
        checkbox: hasExperienceData(userKYC?.experience?.investmentFund),
      },
    },
  };

  return (
    <div className="TabKYC">
      <ConnectedTabKYCForm
        initialValues={modifiedInitialValues}
        experienceForex={hasExperienceData(userKYC?.experience?.forex)}
        experienceDerivative={hasExperienceData(userKYC?.experience?.derivative)}
        experienceSecurities={hasExperienceData(userKYC?.experience?.securities)}
        experienceInvestmentFund={hasExperienceData(userKYC?.experience?.investmentFund)}
      />
    </div>
  );
};

export default TabKYC;
